import { MessageSquare, MessageSquareWarning } from "lucide-react";
import type { ReactNode } from "react";
import { LoadingSpinner } from "@/core/components/loading-spinner";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";

export const PackChatState = ({
  title,
  body,
  error,
  loading,
  action,
  compact,
  fill,
}: {
  title?: string;
  body?: string;
  error?: unknown;
  loading?: boolean;
  action?: ReactNode;
  /** For a narrow list column, where the full state block overflows. */
  compact?: boolean;
  /**
   * For a state that owns the whole pane.
   *
   * Sits above the middle rather than at either end: pinned to the top of a
   * tall card it reads as content that failed to load, and at the exact centre
   * it drifts away from the tabs it belongs to.
   */
  fill?: boolean;
}) => {
  // A fraction down from the top of the pane, not centred in it: on a tall card
  // the exact centre drifts away from the tabs the state belongs to, and the top
  // reads as content that failed to load. One class per case, because `py-10`
  // and a separate `pt-` are the same property and stylesheet order, not source
  // order, decides which wins.
  const box = fill
    ? "h-full min-h-64 pt-[16vh] pb-10"
    : compact
      ? "min-h-32"
      : "min-h-64";

  if (loading) {
    return (
      <Stack
        align="center"
        justify={fill ? "start" : "center"}
        className={cn("w-full", box)}
      >
        <LoadingSpinner />
      </Stack>
    );
  }

  const stateTitle = title ?? "PackChat couldn't be loaded";
  const stateBody = body ?? "Try again. If it keeps failing, contact support.";

  return (
    <Stack
      fullWidth
      align="center"
      justify={fill ? "start" : "center"}
      gap="xs"
      className={cn("px-6 text-center", {
        [box]: fill,
        "min-h-32 py-6": !fill && compact,
        "min-h-64 py-10": !fill && !compact,
      })}
    >
      {error ? (
        <MessageSquareWarning
          className="mb-1 size-6 text-destructive"
          aria-hidden
        />
      ) : (
        <MessageSquare
          className="mb-1 size-6 text-stroke-secondary"
          aria-hidden
        />
      )}
      <Typography variant={compact ? "subtitleW3" : "subtitleW2"}>
        {stateTitle}
      </Typography>
      <Typography
        variant="bodyW3"
        textColor="secondary"
        className="max-w-[42ch]"
      >
        {stateBody}
      </Typography>
      {action}
    </Stack>
  );
};
